import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { directoryMeta, directorySearch, directoryClaim, subscribeComingSoon } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";
const CARD = "#FFFFFF";
const INK = "#1A1A2E";
const MUTED = "#5b5e69";

// Visual 5-star rating row. Handles half-star fills via a clipped overlay.
function StarRow({ value = 0, size = 14 }) {
  const clamped = Math.max(0, Math.min(5, value));
  const pct = (clamped / 5) * 100;
  const stars = "★★★★★";
  return (
    <span
      aria-label={`${clamped.toFixed(1)} out of 5 stars`}
      style={{
        position: "relative", display: "inline-block",
        fontSize: size, lineHeight: 1, letterSpacing: 1,
        fontFamily: "Arial, sans-serif",
      }}
    >
      <span style={{ color: "#e5e5e5" }}>{stars}</span>
      <span
        style={{
          position: "absolute", top: 0, left: 0, width: `${pct}%`,
          overflow: "hidden", whiteSpace: "nowrap", color: GOLD,
        }}
      >
        {stars}
      </span>
    </span>
  );
}


export default function FindAProPage() {
  const [meta, setMeta] = useState({ trades: [], cities: [], top_cities: [] });
  const [trade, setTrade] = useState("");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [claiming, setClaiming] = useState(null); // listing id currently claiming
  const [claimForm, setClaimForm] = useState({ email: "", phone: "" });
  const [claimMsg, setClaimMsg] = useState("");

  useEffect(() => {
    document.title = "Find a Pro — Rhode Island Contractor Directory | NearNow";
    directoryMeta().then(setMeta).catch(() => {});
  }, []);

  const partners = useMemo(
    () => results.filter((r) => r.is_nearnow_partner),
    [results]
  );
  const others = useMemo(
    () => results.filter((r) => !r.is_nearnow_partner),
    [results]
  );

  const runSearch = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setSearched(true);
    try {
      const data = await directorySearch({
        trade: trade || undefined,
        city: city || undefined,
        q: q || undefined,
        limit: 100,
      });
      setResults(data.results || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const openClaim = (id) => {
    setClaiming(id);
    setClaimForm({ email: "", phone: "" });
    setClaimMsg("");
  };

  const submitClaim = async (id) => {
    setClaimMsg("");
    try {
      const r = await directoryClaim(id, claimForm);
      if (r?.ok) {
        setClaimMsg("Thanks! We'll verify and reach out shortly. Continue to signup →");
        setTimeout(() => (window.location.href = "/pricing"), 1500);
      } else {
        setClaimMsg(r?.error === "already_verified"
          ? "This listing is already a verified NearNow partner."
          : "Please add an email or phone number.");
      }
    } catch (err) {
      setClaimMsg(err?.response?.data?.detail || err?.message || "Something went wrong.");
    }
  };

  return (
    <div style={pageWrap} data-testid="find-a-pro-page">
      <header style={heroWrap}>
        <div style={{ maxWidth: 980, margin: "0 auto", padding: "0 20px" }}>
          <div style={eyebrow}>NEARNOW · RHODE ISLAND</div>
          <h1 style={h1}>FIND A PRO. FAST.</h1>
          <p style={heroSub}>
            Every licensed home-services contractor in Rhode Island — searchable in one place.
            <br />
            <strong style={{ color: GOLD }}>★ NearNow Verified Partners</strong> are pinned to the top.
          </p>
        </div>
      </header>

      <div style={{ maxWidth: 980, margin: "-40px auto 0", padding: "0 20px" }}>
        <form onSubmit={runSearch} style={searchCard} data-testid="find-a-pro-search-form">
          <div style={searchRow}>
            <div style={{ flex: 1 }}>
              <Label>TRADE</Label>
              <select value={trade} onChange={(e) => setTrade(e.target.value)}
                      data-testid="find-a-pro-trade-select" style={selectStyle}>
                <option value="">All trades</option>
                {meta.trades.map((t) => (
                  <option key={t.code} value={t.code}>{t.label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <Label>CITY</Label>
              <select value={city} onChange={(e) => setCity(e.target.value)}
                      data-testid="find-a-pro-city-select" style={selectStyle}>
                <option value="">All RI cities</option>
                {meta.cities.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1.4 }}>
              <Label>KEYWORD (OPTIONAL)</Label>
              <input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder='e.g. "emergency" or "family owned"'
                     data-testid="find-a-pro-q-input" style={inputStyle} />
            </div>
            <button type="submit" data-testid="find-a-pro-search-button" style={searchBtn}
                    disabled={loading}>
              {loading ? "Searching…" : "SEARCH"}
            </button>
          </div>
        </form>

        <div style={{ marginTop: 30 }}>
          {!searched && (
            <p style={{ color: MUTED, textAlign: "center", padding: "40px 12px" }}>
              Pick a trade or city (or just search everything) to see contractors.
            </p>
          )}

          {searched && !loading && results.length === 0 && (
            <div data-testid="find-a-pro-empty" style={emptyBox}>
              <h3 style={{ color: NAVY, marginBottom: 6 }}>No contractors yet for that search.</h3>
              <p style={{ color: MUTED, fontSize: 14 }}>
                Our directory is refreshed weekly. Try a nearby city, or check back soon.
              </p>
            </div>
          )}

          {partners.length > 0 && (
            <section style={{ marginBottom: 40 }} data-testid="find-a-pro-partners-section">
              <SectionHeader
                gold
                title="★ VERIFIED NEARNOW PARTNERS"
                subtitle="These pros signed up with NearNow. Direct SMS + priority response."
              />
              <div style={grid}>
                {partners.map((r) => (
                  <ContractorCard key={r.id} row={r} onClaim={openClaim}
                                  claiming={claiming === r.id}
                                  claimForm={claimForm} setClaimForm={setClaimForm}
                                  onSubmitClaim={submitClaim}
                                  claimMsg={claimMsg}
                                  onCancelClaim={() => setClaiming(null)} />
                ))}
              </div>
            </section>
          )}

          {others.length > 0 && (
            <section data-testid="find-a-pro-others-section">
              <SectionHeader
                title="MORE RHODE ISLAND CONTRACTORS"
                subtitle={`${others.length} more pros in our directory. Not yet verified — but they can be.`}
              />
              <div style={grid}>
                {others.map((r) => (
                  <ContractorCard key={r.id} row={r} onClaim={openClaim}
                                  claiming={claiming === r.id}
                                  claimForm={claimForm} setClaimForm={setClaimForm}
                                  onSubmitClaim={submitClaim}
                                  claimMsg={claimMsg}
                                  onCancelClaim={() => setClaiming(null)} />
                ))}
              </div>
            </section>
          )}
        </div>

        <div style={{ margin: "60px 0 40px", textAlign: "center" }}>
          <div style={{ color: NAVY, fontSize: 13, letterSpacing: 2, fontWeight: 700, marginBottom: 10 }}>
            ARE YOU A CONTRACTOR?
          </div>
          <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 34, color: NAVY, margin: "0 0 12px" }}>
            GET VERIFIED. GET PINNED TO THE TOP.
          </h2>
          <p style={{ color: MUTED, maxWidth: 620, margin: "0 auto 22px" }}>
            NearNow-verified partners appear above every other RI contractor for their trade + city,
            get a gold badge, and receive customer requests by SMS.
          </p>
          <Link to="/pricing" data-testid="find-a-pro-get-verified-cta"
                style={ctaBtn}>
            SEE PRICING & GET VERIFIED →
          </Link>
        </div>

        <HomeownerWaitlistSection meta={meta} />
      </div>
    </div>
  );
}

// Homeowner-side waitlist — moved off homepage per PM22 repositioning.
function HomeownerWaitlistSection({ meta }) {
  const [email, setEmail] = useState("");
  const [service, setService] = useState("plumbing");
  const [city, setCity] = useState("Providence");
  const [status, setStatus] = useState("idle");
  const [topPartner, setTopPartner] = useState(null);

  const join = async () => {
    if (!email) return;
    setStatus("sending");
    try {
      const r = await subscribeComingSoon({
        email, city, state: "RI",
        service_category: service,
        source_page: `/find-a-pro/waitlist-${service}-${city.toLowerCase().replace(/\s+/g, "-")}`,
      });
      const partner = r?.contractor_lead_dispatch?.top_partner;
      setTopPartner(partner || null);
      setStatus("sent"); setEmail("");
      setTimeout(() => { setStatus("idle"); setTopPartner(null); }, 12000);
    } catch { setStatus("error"); }
  };

  return (
    <section data-testid="homeowner-waitlist" style={{
      marginTop: 30, background: "#0A1628", borderRadius: 16, padding: "36px 24px",
      color: "#fff", textAlign: "center"
    }}>
      <div style={{ color: "#D4AF37", letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>
        DON&apos;T SEE THE PRO YOU NEED?
      </div>
      <h2 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 34, color: "#fff", margin: "8px 0 8px" }}>
        WE&apos;LL TEXT YOU WHEN ONE&apos;S LIVE.
      </h2>
      <p style={{ color: "#cbd0db", maxWidth: 560, margin: "0 auto 18px" }}>
        Tell us the trade and the town. No spam. One text when a verified NearNow contractor covers your area.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", maxWidth: 560, margin: "0 auto" }}>
        <select value={service} onChange={(e) => setService(e.target.value)}
                data-testid="waitlist-service" style={{ flex: "1 1 150px", padding: "12px 14px", borderRadius: 10, border: "none", fontSize: 15 }}>
          {(meta.trades || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
        </select>
        <select value={city} onChange={(e) => setCity(e.target.value)}
                data-testid="waitlist-city" style={{ flex: "1 1 170px", padding: "12px 14px", borderRadius: 10, border: "none", fontSize: 15 }}>
          {(meta.cities || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <input placeholder="you@email.com" value={email}
             onChange={(e) => setEmail(e.target.value)}
             data-testid="waitlist-email"
             style={{ marginTop: 8, width: "100%", maxWidth: 560, padding: "12px 14px", borderRadius: 10, border: "none", fontSize: 15 }} />
      <div style={{ marginTop: 10 }}>
        <button onClick={join} disabled={status === "sending" || !email}
                data-testid="waitlist-submit"
                style={{ background: "#D4AF37", color: "#0A1628", border: "none",
                         padding: "14px 26px", borderRadius: 12, fontWeight: 800, letterSpacing: 2,
                         fontSize: 15, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" }}>
          {status === "sending" ? "JOINING…" : status === "sent" ? "YOU'RE ON THE LIST!" : "NOTIFY ME →"}
        </button>
      </div>
      {status === "sent" && topPartner?.business_name && (
        <div data-testid="waitlist-partner-match" style={{
          marginTop: 14, background: "rgba(212,175,55,0.14)", border: "1px solid #D4AF37",
          borderRadius: 10, padding: 14, textAlign: "left", maxWidth: 560, margin: "14px auto 0"
        }}>
          <div style={{ color: "#D4AF37", fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2, fontSize: 12 }}>
            ★ VERIFIED PARTNER DISPATCHED
          </div>
          <div style={{ color: "#fff", fontSize: 15, marginTop: 4 }}>
            <strong>{topPartner.business_name}</strong> — your top-rated NearNow-verified {topPartner.trade} in {topPartner.city} — just got your request. Expect a call or text within minutes.
          </div>
          <div style={{ color: "#cbd0db", fontSize: 12, marginTop: 8, fontStyle: "italic" }}>
            ✉️ Delivery receipt sent to your email. Reply to it if you don&apos;t hear back within 24h.
          </div>
        </div>
      )}
      {status === "sent" && !topPartner?.business_name && (
        <div data-testid="waitlist-generic-receipt" style={{
          marginTop: 14, background: "rgba(255,255,255,0.05)", border: "1px solid #3a4258",
          borderRadius: 10, padding: 12, textAlign: "left", maxWidth: 560, margin: "14px auto 0"
        }}>
          <div style={{ color: "#cbd0db", fontSize: 13 }}>
            You&apos;re on the NearNow list. We&apos;ll text you the moment a verified pro covers your area.
          </div>
        </div>
      )}
    </section>
  );
}

function ContractorCard({ row, onClaim, claiming, claimForm, setClaimForm,
                          onSubmitClaim, claimMsg, onCancelClaim }) {
  const isPartner = row.is_nearnow_partner;
  return (
    <div style={isPartner ? partnerCard : plainCard}
         data-testid={isPartner ? `partner-card-${row.id}` : `imported-card-${row.id}`}>
      {isPartner && (
        <div style={badge} data-testid={`verified-badge-${row.id}`}>
          ★ VERIFIED PARTNER
        </div>
      )}
      <h3 style={cardTitle}>{row.name}</h3>
      <div style={{ color: MUTED, fontSize: 13, marginBottom: 8 }}>
        {row.trade_label || row.trade} · {row.city || row.formatted_address?.split(",")[1]?.trim()}
      </div>
      {row.rating != null && (
        <div
          data-testid={`stars-${row.id}`}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            fontSize: 13, color: INK, marginBottom: 10,
          }}
        >
          <StarRow value={Number(row.rating)} />
          <strong style={{ color: NAVY }}>{Number(row.rating).toFixed(1)}</strong>
          {row.review_count ? (
            <a
              href={row.google_maps_uri || `https://www.google.com/search?q=${encodeURIComponent(row.name + " " + (row.city || "Rhode Island"))}`}
              target="_blank" rel="noreferrer"
              style={{ color: MUTED, textDecoration: "underline dotted" }}
              data-testid={`reviews-link-${row.id}`}
            >
              ({row.review_count} Google reviews)
            </a>
          ) : (
            <span style={{ color: MUTED }}>· new listing</span>
          )}
        </div>
      )}
      <div style={{ color: INK, fontSize: 13, marginBottom: 12 }}>
        {row.formatted_address}
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {row.phone && (
          <a href={`tel:${row.phone.replace(/\s/g, "")}`}
             data-testid={`call-btn-${row.id}`}
             style={isPartner ? btnPrimary : btnPlain}>
            CALL {row.phone_national || row.phone}
          </a>
        )}
        {row.website && (
          <a href={row.website} target="_blank" rel="noreferrer"
             data-testid={`site-btn-${row.id}`}
             style={btnGhost}>
            WEBSITE
          </a>
        )}
        {row.google_maps_uri && (
          <a href={row.google_maps_uri} target="_blank" rel="noreferrer"
             data-testid={`map-btn-${row.id}`}
             style={btnGhost}>
            MAP
          </a>
        )}
      </div>

      {!isPartner && !claiming && (
        <button type="button" onClick={() => onClaim(row.id)}
                data-testid={`claim-btn-${row.id}`}
                style={claimBtn}>
          → CLAIM & GET VERIFIED
        </button>
      )}

      {claiming && (
        <div style={claimBox} data-testid={`claim-form-${row.id}`}>
          <div style={{ fontSize: 12, letterSpacing: 2, fontWeight: 700, color: NAVY, marginBottom: 8 }}>
            OWN THIS BUSINESS?
          </div>
          <input placeholder="Your business email"
                 data-testid={`claim-email-input-${row.id}`}
                 value={claimForm.email}
                 onChange={(e) => setClaimForm((f) => ({ ...f, email: e.target.value }))}
                 style={{ ...inputStyle, marginBottom: 6 }} />
          <input placeholder="Your phone number"
                 data-testid={`claim-phone-input-${row.id}`}
                 value={claimForm.phone}
                 onChange={(e) => setClaimForm((f) => ({ ...f, phone: e.target.value }))}
                 style={{ ...inputStyle, marginBottom: 6 }} />
          {claimMsg && <p style={{ fontSize: 12, color: NAVY, marginBottom: 8 }}>{claimMsg}</p>}
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={() => onSubmitClaim(row.id)}
                    data-testid={`claim-submit-${row.id}`}
                    style={{ ...btnPrimary, flex: 1 }}>SUBMIT</button>
            <button type="button" onClick={onCancelClaim}
                    data-testid={`claim-cancel-${row.id}`}
                    style={{ ...btnGhost, flex: 1 }}>CANCEL</button>
          </div>
        </div>
      )}
    </div>
  );
}

const SectionHeader = ({ title, subtitle, gold }) => (
  <div style={{ marginBottom: 18 }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
      <h2 style={{
        fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 26,
        letterSpacing: 2, color: gold ? GOLD : NAVY, margin: 0
      }}>{title}</h2>
    </div>
    <div style={{ color: MUTED, fontSize: 13, marginTop: 4 }}>{subtitle}</div>
    <div style={{ height: 2, background: gold ? GOLD : "#e5e7ee", marginTop: 8 }} />
  </div>
);

const Label = ({ children }) => (
  <div style={{ color: NAVY, fontSize: 10, letterSpacing: 2, fontWeight: 700, marginBottom: 5 }}>
    {children}
  </div>
);

const pageWrap = { background: BG, minHeight: "100vh", paddingBottom: 60 };
const heroWrap = { background: NAVY, padding: "50px 20px 80px", textAlign: "center", borderBottom: `4px solid ${GOLD}` };
const eyebrow = { color: GOLD, letterSpacing: 4, fontSize: 12, fontWeight: 700, marginBottom: 6 };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 3, color: "#fff", fontSize: 56, margin: 0 };
const heroSub = { color: "#c8ccd7", fontSize: 16, marginTop: 12, maxWidth: 720, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 };
const searchCard = { background: CARD, border: `2px solid ${GOLD}`, borderRadius: 16, padding: 20, boxShadow: "0 20px 40px -20px rgba(10,22,40,0.2)" };
const searchRow = { display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" };
const inputStyle = { width: "100%", padding: "12px 14px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 15, outline: "none", boxSizing: "border-box" };
const selectStyle = { ...inputStyle, background: "#fff" };
const searchBtn = { background: GOLD, color: NAVY, border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 2, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", height: 46, alignSelf: "flex-end", flex: "0 0 auto" };

const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 };
const partnerCard = { background: CARD, border: `2px solid ${GOLD}`, borderRadius: 14, padding: 18, position: "relative", boxShadow: "0 12px 30px -20px rgba(212,175,55,0.5)" };
const plainCard = { background: CARD, border: "1px solid #e5e7ee", borderRadius: 14, padding: 18 };
const badge = { position: "absolute", top: -10, right: 12, background: GOLD, color: NAVY, fontSize: 10, fontWeight: 800, letterSpacing: 2, padding: "4px 10px", borderRadius: 6, fontFamily: "Bebas Neue, Arial, sans-serif" };
const cardTitle = { color: NAVY, fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 1, fontSize: 22, margin: "4px 0 4px" };
const btnPrimary = { background: GOLD, color: NAVY, textDecoration: "none", padding: "9px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, fontFamily: "Bebas Neue, Arial, sans-serif", border: "none", cursor: "pointer" };
const btnPlain = { background: NAVY, color: "#fff", textDecoration: "none", padding: "9px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, fontFamily: "Bebas Neue, Arial, sans-serif" };
const btnGhost = { background: "transparent", color: NAVY, textDecoration: "none", padding: "9px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, letterSpacing: 1.5, fontFamily: "Bebas Neue, Arial, sans-serif", border: `1.5px solid ${NAVY}`, cursor: "pointer" };
const claimBtn = { display: "block", width: "100%", background: "transparent", color: GOLD, border: `1.5px dashed ${GOLD}`, padding: "10px", borderRadius: 8, fontWeight: 700, letterSpacing: 1.5, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer", marginTop: 6 };
const claimBox = { background: "#FFFBEA", border: `1px solid ${GOLD}`, borderRadius: 10, padding: 12, marginTop: 10 };
const emptyBox = { background: CARD, border: "1px dashed #d0d3da", borderRadius: 14, padding: "40px 20px", textAlign: "center" };
const ctaBtn = { display: "inline-block", background: GOLD, color: NAVY, textDecoration: "none", padding: "16px 28px", borderRadius: 12, fontWeight: 800, letterSpacing: 2, fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif" };
